import { useEffect } from "react";

import { useHomeLocation } from "@/hooks/useHomeLocation";
import { useViews } from "@/hooks/useViews";

import { TView } from "@/types";

export const LAST_VIEW_KEY = "chronos.lastViewId";

export const readLastViewId = (): number | undefined => {
	try {
		const raw = window.localStorage.getItem(LAST_VIEW_KEY);
		const id = raw ? Number(raw) : NaN;
		return Number.isInteger(id) && id > 0 ? id : undefined;
	} catch {
		return undefined;
	}
};

export const writeLastViewId = (id: number) => {
	try {
		window.localStorage.setItem(LAST_VIEW_KEY, String(id));
	} catch {
		// Private mode, quota: the default just falls back to the first one.
	}
};

/**
 * The view the home page shows: the URL's, else the last one visited
 * (remembered per browser), else the first of the list - archived views
 * are never landed on, only reached by their URL. Undefined only
 * while the list loads. A URL naming a view the user cannot see (a
 * share withdrawn, a typo) falls back the same way, flagged `isUnknown`
 * so the page can rewrite its address.
 */
export const useActiveView = (): {
	view: TView | undefined;
	/** The URL names a view that is not in the list. */
	isUnknown: boolean;
	isLoading: boolean;
} => {
	const { views, isLoading } = useViews();
	const { viewId } = useHomeLocation();

	const fromUrl = viewId ? views.find((c) => c.id === viewId) : undefined;
	const lastId = readLastViewId();
	const active = views.filter((c) => !c.isArchived);
	const view =
		fromUrl ?? active.find((c) => c.id === lastId) ?? active[0] ?? views[0];

	useEffect(() => {
		if (fromUrl) writeLastViewId(fromUrl.id);
	}, [fromUrl]);

	return {
		view,
		isUnknown: !isLoading && !!viewId && !fromUrl,
		isLoading,
	};
};
